// src/helper/division_helper.js

import { loginStudent } from './student_helper';

// Cache for divisions by phone number
const divisionsCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Function to extract division from error message
export const extractDivisionFromError = (errorMsg) => {
    if (!errorMsg) return null;
    
    console.log('Parsing error message for division:', errorMsg);
    
    // Pattern 1: "Division 'MundhelaKalan' not found. Available divisions: ..."
    const pattern1 = /Division '([^']+)' not found/i;
    const match1 = errorMsg.match(pattern1);
    if (match1 && match1[1]) {
        return match1[1].trim();
    }
    
    // Pattern 2: Look for the division name in quotes
    const pattern2 = /'([^']+)'/;
    const match2 = errorMsg.match(pattern2);
    if (match2 && match2[1] && !match2[1].includes('Available') && !match2[1].includes('divisions')) {
        return match2[1].trim();
    }
    
    // Pattern 3: Look for the word before "not found"
    const pattern3 = /(\w+)\s+not found/i;
    const match3 = errorMsg.match(pattern3);
    if (match3 && match3[1] && match3[1].length > 2) {
        return match3[1].trim();
    }
    
    return null;
};

// Function to extract available divisions list from error
export const extractAvailableDivisions = (errorMsg) => {
    if (!errorMsg) return [];
    
    const pattern = /Available divisions?:?\s*(.*?)(?:\.|$|\n)/i;
    const match = errorMsg.match(pattern);
    
    if (match && match[1]) {
        return match[1].split(',').map(d => d.trim()).filter(d => d.length > 0);
    }
    
    return [];
};

// Function to auto-detect division by attempting login
export const autoDetectDivision = async (phone) => {
    if (!phone || phone.length !== 10) {
        return { success: false, division: null, message: 'Invalid phone number' };
    }
    
    // Check cache first
    if (divisionsCache.has(phone)) {
        const cached = divisionsCache.get(phone);
        if (Date.now() - cached.timestamp < CACHE_DURATION) {
            console.log('Using cached division for:', phone, cached.division);
            return { 
                success: true, 
                division: cached.division,
                fromCache: true,
                availableDivisions: cached.availableDivisions || []
            };
        }
    }
    
    try {
        // Try different formats to trigger the division error
        const testFormats = [
            { email: phone, desc: 'Phone only' },
            { email: `${phone}-`, desc: 'Phone with hyphen' },
            { email: `${phone}-test`, desc: 'Phone with test division' },
            { email: `${phone}-dummy`, desc: 'Phone with dummy division' }
        ];
        
        let detectedDivision = null;
        let availableDivisions = [];
        let lastError = '';
        
        for (const format of testFormats) {
            try {
                console.log(`Testing format: ${format.desc} - ${format.email}`);
                
                const loginData = {
                    email: format.email,
                    password: "dummy_password_123"
                };
                
                await loginStudent(loginData);
                
            } catch (error) {
                const errorMsg = error?.response?.data?.message || error?.message || '';
                lastError = errorMsg;
                console.log(`Error from ${format.desc}:`, errorMsg);
                
                // Try to extract division from error
                const division = extractDivisionFromError(errorMsg);
                if (division) {
                    detectedDivision = division;
                    console.log(`Found division from ${format.desc}:`, division);
                }
                
                // Also collect available divisions if present
                const available = extractAvailableDivisions(errorMsg);
                if (available.length > 0) {
                    availableDivisions = available;
                }
                
                // If we found a division, we can stop
                if (detectedDivision) {
                    break;
                }
            }
        }
        
        // Cache the result if found
        if (detectedDivision) {
            divisionsCache.set(phone, {
                division: detectedDivision,
                availableDivisions: availableDivisions,
                timestamp: Date.now()
            });
            
            return { 
                success: true, 
                division: detectedDivision,
                availableDivisions: availableDivisions,
                message: `Division detected: ${detectedDivision}`
            };
        } else {
            return { 
                success: false, 
                division: null,
                availableDivisions: availableDivisions,
                message: lastError || 'Could not detect division'
            };
        }
        
    } catch (error) {
        console.error('Error in autoDetectDivision:', error);
        return { 
            success: false, 
            division: null, 
            message: error?.message || 'Failed to detect division'
        };
    }
};

// Clear cache for a specific phone
export const clearDivisionCache = (phone) => {
    if (phone && divisionsCache.has(phone)) {
        divisionsCache.delete(phone);
    }
};
